/**
 * Popup codice di benvenuto: email -> codice nello stesso popup, senza ricaricare la pagina.
 * Comparsa: dopo un ritardo o a una percentuale di scorrimento, mai sopra il banner cookie. Se chiuso senza iscriversi
 * ricompare solo in home, al massimo altre due volte (dopo 10 minuti e dopo un giorno); dopo la terza chiusura torna
 * trascorsi i giorni impostati. Dopo l'iscrizione non ricompare piu.
 * Invio: il form 'customer' parte in background. Se la protezione antispam di Shopify e attiva, il suo captcha invisibile
 * genera il token e poi chiama form.submit(): quella chiamata e deviata sull'invio in background.
 * localStorage sempre dentro try/catch: se non e disponibile il popup funziona comunque, senza memoria.
 */

const KEY = 'gdm_email_popup';
const DAY = 24 * 60 * 60 * 1000;
const REOPEN_GAPS = [10 * 60 * 1000, DAY]; // attese in home dopo la 1a e la 2a chiusura
const EXCLUDED = /^\/([a-z]{2}(-[a-z]{2})?\/)?(cart|checkouts?|account|policies|password|challenge)(\/|$)/;

const read = () => {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || 'null');
  } catch (error) {
    return null;
  }
};
const write = (state) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch (error) {
    /* memoria non disponibile */
  }
};

class EmailPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    if (!this.dialog || !this.form) return;
    this.formStep = this.querySelector('[data-step-form]');
    this.codeStep = this.querySelector('[data-step-code]');
    this.error = this.querySelector('[data-error]');
    this.button = this.form.querySelector('[type="submit"]');

    this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => this.onClose());
    this.querySelector('[data-copy]')?.addEventListener('click', () => this.copy());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.submit = () => this.send();

    if (window.Shopify?.designMode) return;
    if (this.querySelector('[data-posted]')) {
      // ritorno da un invio normale andato a buon fine
      this.showCode();
      this.open();
      return;
    }
    if (this.dataset.enabled === 'true' && this.allowed()) this.schedule();
  }

  reopenMs() {
    return (Number(this.dataset.reopenDays) || 30) * DAY;
  }

  allowed() {
    if (EXCLUDED.test(window.location.pathname.toLowerCase())) return false;
    const state = read();
    if (!state) return true;
    if (state.status === 'subscribed') return false;
    const elapsed = Date.now() - Number(state.at);
    if (elapsed >= this.reopenMs()) return true;
    const count = Number(state.count) || 1;
    if (count > REOPEN_GAPS.length || this.dataset.pageType !== 'index') return false;
    return elapsed >= REOPEN_GAPS[count - 1];
  }

  schedule() {
    const percent = Number(this.dataset.scroll) || 0;
    const fire = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll, true);
      this.openAfterCookieBanner();
    };
    const onScroll = () => {
      const page = document.querySelector('.page-wrapper');
      const box = page && page.scrollHeight > page.clientHeight ? page : document.scrollingElement;
      const max = box.scrollHeight - box.clientHeight;
      if (max > 0 && (box.scrollTop / max) * 100 >= percent) fire();
    };
    const timer = window.setTimeout(fire, (Number(this.dataset.delay) || 0) * 1000);
    if (percent > 0) window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  }

  // Mai sopra il carrello o un altro pannello aperto, e mai a chi ha già qualcosa nel carrello: non deve coprire "Vai al pagamento"
  openAfterCookieBanner() {
    const started = Date.now();
    const check = () => {
      const banner = document.getElementById('shopify-pc__banner');
      const visible = banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden';
      const busy = [...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== this.dialog);
      if (!visible && !busy) {
        this.cartHasItems().then((hasItems) => {
          if (!hasItems) this.open();
        });
      } else if (Date.now() - started < 60000) window.setTimeout(check, 500);
    };
    check();
  }

  async cartHasItems() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      return cart.item_count > 0;
    } catch {
      return false;
    }
  }

  open() {
    if (this.dialog.open) return;
    this.previousFocus = document.activeElement;
    this.dialog.showModal();
    (this.subscribed ? this.codeStep : this.dialog).focus({ preventScroll: true });
  }

  onClose() {
    this.previousFocus?.focus?.({ preventScroll: true });
    if (this.subscribed) return;
    const previous = read();
    const recent = previous?.status === 'closed' && Date.now() - Number(previous.at) < this.reopenMs();
    write({ status: 'closed', at: Date.now(), count: recent ? (Number(previous.count) || 1) + 1 : 1 });
  }

  onSubmit(event) {
    const email = this.form.elements['contact[email]'];
    if (!email.value.trim() || !email.checkValidity()) {
      event.preventDefault();
      this.error.hidden = false;
      email.focus();
      return;
    }
    this.error.hidden = true;
    // con il captcha di Shopify attivo l'invio prosegue da lui e arriva a form.submit() (deviato su send)
    const captcha = this.form.dataset.hcaptchaBound || this.form.dataset.recaptchaBound || this.form.dataset.cptcha;
    if (!captcha || this.sending) {
      event.preventDefault();
      this.send();
    }
  }

  async send() {
    if (this.sending) return;
    this.sending = true;
    this.button.disabled = true;
    let fallback = false;
    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (response.ok && response.url.includes('customer_posted=true')) {
        this.showCode();
        this.codeStep.focus({ preventScroll: true });
      } else if (response.ok && !response.url.includes('/challenge')) {
        this.error.hidden = false; // email rifiutata da Shopify
      } else {
        fallback = true;
      }
    } catch (error) {
      fallback = true;
    }
    this.sending = false;
    this.button.disabled = false;
    // verifica aggiuntiva richiesta da Shopify: invio normale, al ritorno il popup mostra il codice
    if (fallback) HTMLFormElement.prototype.submit.call(this.form);
  }

  showCode() {
    this.subscribed = true;
    write({ status: 'subscribed', at: Date.now() });
    this.formStep.hidden = true;
    this.codeStep.hidden = false;
  }

  async copy() {
    const code = this.querySelector('[data-code]')?.textContent.trim();
    const label = this.querySelector('[data-copy-label]');
    const button = this.querySelector('[data-copy]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-code]'));
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      return;
    }
    const original = label.textContent;
    label.textContent = button.dataset.copied;
    this.querySelector('[data-status]').textContent = button.dataset.copied;
    window.setTimeout(() => (label.textContent = original), 2500);
  }
}

if (!customElements.get('email-popup')) customElements.define('email-popup', EmailPopup);
